import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
  Table,
  Spinner,
  Alert,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  getMembers,
  getCommittees,
  updateMember,
  deleteMember,
} from "../api/api";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faPlus } from "@fortawesome/free-solid-svg-icons";
import LotusIcon from "../components/LotusIcon";

const MembersList = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const [members, setMembers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    committee: "",
    role: "",
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    committee: "",
    position: "",
    role: "member",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  // Role options
  const roleOptions = [
    { value: "member", label: "Member" },
    { value: "admin", label: "Admin" },
    { value: "director", label: "Director" },
  ];

  useEffect(() => {
    fetchData();
  }, [isAuthenticated, filters]);

  const fetchData = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch members with filters
      const membersRes = await getMembers(filters);
      setMembers(membersRes || []);

      // Fetch committees for filter dropdown
      const committeesRes = await getCommittees();
      setCommittees(committeesRes || []);
    } catch (err) {
      console.error("Error fetching members:", err);
      setError("Failed to load members. Please try again later.");
      setMembers([]);
      setCommittees([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
      committee: "",
      role: "",
    });
  };

  const handleOpenModal = (member) => {
    setSelectedMember(member);

    setFormData({
      name: member.name,
      email: member.email,
      committee: member.committee?._id || "",
      position: member.position || "",
      role: member.role || "member",
    });

    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMember(null);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.committee) {
      newErrors.committee = "Committee is required";
    }

    if (!formData.position.trim()) {
      newErrors.position = "Position is required";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || !selectedMember) {
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await updateMember(selectedMember._id, formData);

      // Update the members list
      setMembers(
        members.map((m) => (m._id === selectedMember._id ? res.data : m))
      );

      handleCloseModal();
    } catch (err) {
      console.error("Error updating member:", err);
      setFormErrors({
        submit:
          err.response?.data?.message ||
          "Failed to update member. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      await deleteMember(memberToDelete._id);
      setMembers(members.filter((m) => m._id !== memberToDelete._id));
      handleCloseDeleteModal();
    } catch (err) {
      console.error("Error deleting member:", err);
      setError("Failed to delete member. Please try again later.");
      handleCloseDeleteModal();
    }
  };

  // Get committee name by ID
  const getCommitteeName = (committeeId) => {
    const committee = committees.find((c) => c._id === committeeId);
    return committee ? committee.name : "Unknown";
  };

  // Get committee color by ID
  const getCommitteeColor = (committeeId) => {
    const committee = committees.find((c) => c._id === committeeId);
    return committee ? committee.color : "primary";
  };

  return (
    <Container>
      <div className="section-title d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <LotusIcon className="me-3" size={32} />
          <h1 className="mb-0">Members</h1>
        </div>
        <Button variant="primary" onClick={() => handleOpenModal(null)}>
          <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Member
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="search">
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search by name or email"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="committee">
                <Form.Label>Committee</Form.Label>
                <Form.Select
                  name="committee"
                  value={filters.committee}
                  onChange={handleFilterChange}
                >
                  <option value="">All Committees</option>
                  {committees &&
                    committees.map((committee) => (
                      <option key={committee._id} value={committee._id}>
                        {committee.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="role">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  name="role"
                  value={filters.role}
                  onChange={handleFilterChange}
                >
                  <option value="">All Roles</option>
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex justify-content-end">
            <Button variant="outline-secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Error message */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading spinner */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading members...</p>
        </div>
      ) : members && members.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <h3>No members found</h3>
            <p className="text-muted">
              {Object.values(filters).some((val) => val !== "")
                ? "Try adjusting your filters."
                : "No members have been added yet."}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Committee</th>
                  <th>Position</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member._id}>
                    <td>{member.name}</td>
                    <td>{member.email}</td>
                    <td>
                      {member.committee ? (
                        <Badge bg={member.committee.color || "primary"}>
                          {member.committee.name}
                        </Badge>
                      ) : (
                        <Badge bg="secondary">None</Badge>
                      )}
                    </td>
                    <td>{member.position || "N/A"}</td>
                    <td>
                      <Badge
                        bg={
                          member.role === "admin"
                            ? "danger"
                            : member.role === "director"
                            ? "warning"
                            : "info"
                        }
                      >
                        {member.role || "Member"}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleOpenModal(member)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleOpenDeleteModal(member)}
                        disabled={member._id === currentUser?._id}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Edit Member Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Member</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {formErrors.submit && (
              <Alert variant="danger">{formErrors.submit}</Alert>
            )}

            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter name"
                isInvalid={!!formErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email"
                isInvalid={!!formErrors.email}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="committeeEdit">
              <Form.Label>Committee</Form.Label>
              <Form.Select
                name="committee"
                value={formData.committee}
                onChange={handleChange}
                isInvalid={!!formErrors.committee}
              >
                <option value="">Select Committee</option>
                {committees &&
                  committees.map((committee) => (
                    <option key={committee._id} value={committee._id}>
                      {committee.name}
                    </option>
                  ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formErrors.committee}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="position">
              <Form.Label>Position</Form.Label>
              <Form.Control
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="Enter position"
                isInvalid={!!formErrors.position}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.position}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="role">
              <Form.Label>Role</Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={selectedMember?._id === currentUser?._id}
              >
                {roleOptions.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Form.Select>
              {selectedMember?._id === currentUser?._id && (
                <Form.Text className="text-muted">
                  You cannot change your own role.
                </Form.Text>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the member "{memberToDelete?.name}"?
          </p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteMember}>
            Delete Member
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MembersList;
